import { Injectable, inject } from '@angular/core';

import { BehaviorSubject, Observable, catchError, combineLatest, map, of, shareReplay, tap } from 'rxjs';

import {
  Currency,
  CurrencyConfig,
  CurrencyExchangeRate,
  DISPLAY_CURRENCY_COOKIE_NAME
} from '@hdms/shared/common-types';
import { CookieService, EnvironmentService, GQL_RESPONSE_CACHE_TTL_HEADER } from '@hdms/shared/util-core';

import { CurrencyExchangeRateGqlService } from './currency-exchange-rate-gql.service';

// 4h cache TTL for exchange rates GQL response
const EXCHANGE_RATES_CACHE_TTL = 14400;

@Injectable({
  providedIn: 'root'
})
export class CurrencyService {
  private readonly environmentService = inject(EnvironmentService);
  private readonly currencyExchangeRateGqlService = inject(CurrencyExchangeRateGqlService);
  private readonly cookieService = inject(CookieService);
  private readonly changeSubject = new BehaviorSubject<string>(this.environmentService.currency.code);

  getConfig$(): Observable<CurrencyConfig> {
    if (this.environmentService.currencies.length > 1) {
      return combineLatest([this.getExchangeRates$(), this.changeSubject]).pipe(
        map(([currencies, activeCode]) => ({
          activeCurrency: activeCode ? currencies.find(curr => curr.code === activeCode) : currencies[0],
          currencies
        }))
      );
    } else {
      return of({} as CurrencyConfig);
    }
  }

  switchCurrency(code: string): void {
    this.cookieService.set(DISPLAY_CURRENCY_COOKIE_NAME, code.toLowerCase());
    this.changeSubject.next(code);
  }

  protected getExchangeRates$(): Observable<Currency[]> {
    if (this.environmentService.currencies.some(currency => !currency.rate)) {
      const currencyFrom = this.environmentService.currencies.find(currency => currency.primary);
      const currencyTo = this.environmentService.currencies.find(currency => !currency.primary);
      return this.currencyExchangeRateGqlService
        .fetch(
          { currencyFrom: currencyFrom.code, currencyTo: currencyTo.code },
          { context: { headers: { [GQL_RESPONSE_CACHE_TTL_HEADER]: String(EXCHANGE_RATES_CACHE_TTL) } } }
        )
        .pipe(
          map(({ data }) => data.currencyExchangeRate),
          catchError(_ => of(null)),
          map(rates => this.getExchangeRateData(currencyFrom, currencyTo, rates)),
          tap(currencies => (this.environmentService.currencies = currencies)),
          shareReplay(1)
        );
    } else {
      return of(this.environmentService.currencies);
    }
  }

  private getExchangeRateData(currencyFrom: Currency, currencyTo: Currency, rate?: CurrencyExchangeRate): Currency[] {
    return [
      {
        ...currencyFrom,
        base: currencyTo.code,
        rate: rate?.exchangeRateFrom
      },
      {
        ...currencyTo,
        base: currencyFrom.code,
        rate: rate?.exchangeRateTo
      }
    ];
  }
}
